import React, { useState } from 'react';
import { GitBranch } from 'lucide-react';
import FormModal from './FormModal';
import type { CreateFrameworkVersionDTO, UpdateFrameworkVersionDTO, FrameworkVersion } from '@domains/compliance/definition/frameworkVersions';

interface FrameworkVersionModalProps {
  onClose: () => void;
  onSubmit: (version: CreateFrameworkVersionDTO | UpdateFrameworkVersionDTO, selectedVersion?: FrameworkVersion) => Promise<void> | void;
  selectedVersion: FrameworkVersion | null;
  frameworkId: number;
}

export const FrameworkVersionModal: React.FC<FrameworkVersionModalProps> = ({
  onClose,
  onSubmit,
  selectedVersion,
  frameworkId,
}) => {
  const [version, setVersion] = useState<CreateFrameworkVersionDTO | UpdateFrameworkVersionDTO>(
    selectedVersion || {
      framework_id: frameworkId,
      version: '',
    }
  );

  const handleSubmit = async (data: any) => {
    try {
      await onSubmit({ ...version, ...data }, selectedVersion || undefined);
      onClose();
    } catch {
      // error already handled by parent; keep modal open
    }
  };

  const isCreate = !selectedVersion?.id;

  return (
    <FormModal
      title={isCreate ? 'Create Framework Version' : 'Edit Framework Version'}
      subtitle={isCreate ? 'Add a new version to this framework. Set status to Active when ready for mapping.' : 'Modify version details'}
      icon={<GitBranch className="text-white" size={24} />}
      onSubmit={handleSubmit}
      onCancel={onClose}
      submitText={isCreate ? 'Create Version' : 'Update Version'}
      cancelText="Cancel"
      size="large"
    >
      <div className="space-y-5">
        <div>
          <label htmlFor="version" className="block text-sm font-semibold text-slate-700 mb-1.5">
            Version Number <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="version"
            name="version"
            required
            value={version.version}
            onChange={(e) => setVersion({ ...version, version: e.target.value })}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
            placeholder="e.g., 2022, 1.0, v3"
          />
          <p className="text-xs text-slate-400 mt-1.5">Use the year or version number from the standard (e.g., "2022" for ISO 27001:2022)</p>
        </div>

        {!isCreate && (
          <div>
            <label htmlFor="status" className="block text-sm font-semibold text-slate-700 mb-1.5">
              Status
            </label>
            <select
              id="status"
              name="status"
              value={(version as UpdateFrameworkVersionDTO).status || 'draft'}
              onChange={(e) => setVersion({ ...version, status: e.target.value as any })}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none bg-white cursor-pointer text-sm"
            >
              <option value="draft">Draft — Not available for mapping</option>
              <option value="active">Active — Available for mapping and assignment</option>
              <option value="deprecated">Archived — No longer in use</option>
            </select>
            <p className="text-xs text-slate-400 mt-1.5">Only active versions can be mapped to templates and assigned to companies</p>
          </div>
        )}

        {isCreate && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
            <p className="text-sm text-blue-800">
              <strong>Tip:</strong> The version will be created as <strong>Draft</strong>. You can activate it from the frameworks list after creation.
            </p>
          </div>
        )}
      </div>
    </FormModal>
  );
};
